import type { Step as RawStep } from "../../../types/rawProcess.types";
import { dbOps } from "../hooks/dbOps.store";
import { stepModelCreator } from "../../../models/step.model";
import {
  insertItemAtIndex,
  moveItemToAnotherArray,
  reorderItems
} from "@certa/common/utils/array";
import { createStep, type Step } from "@certa/studio-core";
import { addSwimlane } from "./swimlane.actions";
import { importRulesToStudio } from "./rule.actions";
import { Intents } from "@certa/studio-core/src/constants/activityLogs.constants";

export const importSwimlaneTemplateRawSteps = (props: {
  rawSteps: RawStep[];
  processId: string;
  swimlaneId: string;
  templateId: string;
}) => {
  const { rawSteps, processId, swimlaneId, templateId } = props;
  dbOps().transaction(
    () => {
      const swimlane = dbOps().getSwimlane(processId, swimlaneId);

      for (const rawStep of rawSteps) {
        const { step, fieldsMap, conditionGroupMap, conditionMap, ruleMap } =
          stepModelCreator(rawStep, { processId });
        swimlane.steps.push(step.id);
        dbOps().setStep(processId, step);
        dbOps().bulkSetFields(processId, Object.values(fieldsMap));
        importRulesToStudio({
          processId,
          ruleMap,
          conditionMap,
          conditionGroupMap
        });
      }

      dbOps().updateSwimlane(processId, swimlaneId, { steps: swimlane.steps });
    },
    {
      intent: Intents.IMPORT_SWIMLANE_TEMPLATE_IN_EXISTING_SWIMLANE,
      templateId
    }
  );
};

export const importRawStep = (props: {
  rawStep: RawStep;
  processId: string;
  swimlaneId: string;
  index?: number;
  templateId: string;
}) => {
  const { rawStep, processId, swimlaneId, index, templateId } = props;
  return dbOps().transaction(
    () => {
      const { step, fieldsMap, conditionGroupMap, conditionMap, ruleMap } =
        stepModelCreator(rawStep, { processId });

      const swimlane = dbOps().getSwimlane(processId, swimlaneId);
      const newStepSequence = insertItemAtIndex({
        array: swimlane.steps,
        item: step.id,
        index
      });

      dbOps().updateSwimlane(processId, swimlaneId, { steps: newStepSequence });
      dbOps().addStep(processId, step);
      dbOps().bulkSetFields(processId, Object.values(fieldsMap));
      importRulesToStudio({
        processId,
        ruleMap,
        conditionMap,
        conditionGroupMap
      });
    },
    {
      intent: Intents.IMPORT_STEP_TEMPLATE,
      templateId
    }
  );
};

export const addStep = (
  props: {
    processId: string;
    stepData?: Partial<Step>;
    index?: number;
  } & (
    | {
        swimlaneId: string;
        shouldCreateHostSwimlane?: false;
      }
    | {
        swimlaneId?: undefined;
        shouldCreateHostSwimlane: true;
      }
  )
) => {
  const { processId, swimlaneId, stepData, index, shouldCreateHostSwimlane } =
    props;
  const newStep = createStep({ processId, props: stepData });

  return dbOps().transaction(
    () => {
      const hostSwimlaneId = shouldCreateHostSwimlane
        ? addSwimlane({ processId, swimlaneData: { steps: [newStep.id] } })
        : swimlaneId;
      if (!hostSwimlaneId) return [undefined, undefined] as const;

      const swimlane = dbOps().getSwimlane(processId, hostSwimlaneId);
      // If new host swimlane is created, it won't be returned by dbOps().getSwimlane
      // Hence the additional && check
      if (!swimlane && !shouldCreateHostSwimlane)
        return [undefined, undefined] as const;

      dbOps().addStep(processId, newStep);

      // We don't need to push stepId in the newly created host swimlane
      // as it was created with the stepId in the `steps` sequence array
      if (!shouldCreateHostSwimlane) {
        const newStepSequence = insertItemAtIndex({
          array: swimlane.steps,
          item: newStep.id,
          index
        });
        dbOps().updateSwimlane(processId, hostSwimlaneId, {
          steps: newStepSequence
        });
      }

      return [newStep.id, hostSwimlaneId] as const;
    },
    {
      intent: Intents.ADD_STEP
    }
  );
};

export const deleteStep = ({
  processId,
  parentSwimlaneId,
  stepId
}: {
  processId: string;
  parentSwimlaneId: string;
  stepId: string;
}) => {
  dbOps().transaction(
    () => {
      const swimlane = dbOps().getSwimlane(processId, parentSwimlaneId);

      if (!swimlane) return;

      const newStepSequence = swimlane.steps.filter(step => step !== stepId);

      dbOps().updateSwimlane(processId, parentSwimlaneId, {
        steps: newStepSequence
      });
      dbOps().removeStep(processId, stepId); // This runs cascading deletes too!
    },
    { intent: Intents.DELETE_STEP }
  );
};

export const updateStep = ({
  processId,
  stepId,
  stepProps,
  shouldSkipUpdatingModifiedAt
}: {
  processId: string;
  stepId: string;
  stepProps: Partial<Step>;
  shouldSkipUpdatingModifiedAt?: boolean;
}) =>
  dbOps().transaction(
    () => {
      dbOps().updateStep(
        processId,
        stepId,
        stepProps,
        shouldSkipUpdatingModifiedAt
      );
    },
    { intent: Intents.UPDATE_STEP }
  );

export const moveStep = (props: {
  processId: string;
  stepId: string;
  targetSwimlaneId: string;
  targetIndex: number;
}) => {
  const { processId, stepId, targetSwimlaneId, targetIndex } = props;

  return dbOps().transaction(
    () => {
      const step = dbOps().getStep(processId, stepId);
      const sourceSwimlane = dbOps().getSwimlaneByStepId(processId, stepId);
      if (!step || !sourceSwimlane) return;

      const sourceSwimlaneId = sourceSwimlane.id;
      const targetSwimlane = dbOps().getSwimlane(processId, targetSwimlaneId);

      if (!sourceSwimlane || !targetSwimlane) return;

      if (sourceSwimlaneId === targetSwimlaneId) {
        const newStepSequence = reorderItems({
          array: sourceSwimlane.steps,
          item: stepId,
          targetIndex
        });

        dbOps().updateSwimlane(processId, sourceSwimlaneId, {
          steps: newStepSequence
        });
      } else {
        const [newSourceStepSequence, newTargetStepSequence] =
          moveItemToAnotherArray({
            sourceArr: sourceSwimlane.steps,
            targetArr: targetSwimlane.steps,
            item: stepId,
            targetIndex
          });

        dbOps().updateSwimlane(processId, sourceSwimlaneId, {
          steps: newSourceStepSequence
        });
        dbOps().updateSwimlane(processId, targetSwimlaneId, {
          steps: newTargetStepSequence
        });
      }
    },
    {
      intent: Intents.REORDER_STEP
    }
  );
};
